<script setup lang="ts">
import type { InputMessageValue } from '@ui/components/input/InputMessageTypes'
import type { BackgroundType, MessageVariety, SizesType } from '@ui/styles'
import Divider from '@ui/components/divider/Divider.vue'
import IconButton from '@ui/components/icon/IconButton.vue'
import { ButtonVariant, Sizes } from '@ui/styles'
import Minus from '~icons/cue-regular/minus'
import Plus from '~icons/cue-regular/plus'
import Times from '~icons/cue-regular/times'
import InputContainer from '../InputContainer.vue'
import { formatNumberInputValue, limitNumberInputFraction, parseNumberInputValue } from '../numberFormat'

const props = withDefaults(
  defineProps<{
    label?: string
    tooltip?: string
    placeholder?: string | number
    message?: InputMessageValue
    variety?: MessageVariety
    disabled?: boolean
    readonly?: boolean
    stepper?: boolean
    clean?: boolean
    size?: SizesType
    background?: BackgroundType
    min?: number
    max?: number
    step?: number
    unit?: string
    maxFractionDigits?: number
  }>(),
  {
    label: '',
    tooltip: '',
    placeholder: '',
    message: '',
    disabled: false,
    readonly: false,
    stepper: false,
    clean: false,
    size: Sizes.Luke,
    step: 1,
    unit: '',
  },
)

const emit = defineEmits<{ clear: [] }>()
const value = defineModel<number | string>('modelValue', { default: 1 })
const showStepper = computed(() => props.stepper && !props.disabled)
const formattedValue = computed(() => formatNumberInputValue(value.value))
const displayValue = shallowRef(formattedValue.value)
const inputFocused = shallowRef(false)
const inputWidth = computed(() => `calc(${Math.max(displayValue.value.length, 1)}ch + 1px)`)
const input = useTemplateRef<HTMLInputElement>('input')

// The stepper input is only as wide as its value, so a click anywhere around it should still start typing.
function focusInput(event: MouseEvent) {
  if (event.target !== input.value) {
    input.value?.focus()
  }
}

watch(formattedValue, formatted => {
  if (!inputFocused.value) {
    displayValue.value = formatted
  }
})

function sub() {
  if (props.min !== undefined) {
    setValue(Math.max(Number(value.value) - props.step, props.min))
  } else {
    setValue(Number(value.value) - props.step)
  }
}

function add() {
  if (props.max !== undefined) {
    setValue(Math.min(Number(value.value) + props.step, props.max))
  } else {
    setValue(Number(value.value) + props.step)
  }
}

function setValue(nextValue: number | '') {
  if (nextValue !== value.value) {
    value.value = nextValue
  }
}

function updateDisplayValue(event: Event) {
  if (event.target instanceof HTMLInputElement) {
    const input = event.target
    const inputValue = limitNumberInputFraction(input.value, props.maxFractionDigits)
    if (input.value !== inputValue) {
      input.value = inputValue
    }
    displayValue.value = inputValue
    setValue(parseNumberInputValue(inputValue) ?? '')
  }
}

function formatDisplayValue() {
  inputFocused.value = false
  displayValue.value = formattedValue.value
}

function clearValue() {
  setValue('')
  displayValue.value = ''
  emit('clear')
}

const stepprIconSize = computed(() => (props.size === Sizes.R2D2 ? Sizes.R2D2 : Sizes.Leia))
const { min, max } = toRefs(props)
watch([value, min, max], () => {
  if (value.value === '') {
    return
  }
  const minV = props.min !== undefined ? Math.max(props.min, +value.value) : +value.value
  value.value = props.max !== undefined ? Math.min(props.max, minV) : minV
})
</script>

<template lang="pug">
InputContainer.ui-input.single-line(
  :label="label"
  :tooltip="tooltip"
  :message="message"
  :variety="variety"
  :size="size"
  :disabled="disabled"
  :readonly="readonly"
  :background="background"
)
  .input-content.flex-between-center.w-100p
    IconButton.icon(
      v-if="showStepper"
      :name="Minus"
      :size="stepprIconSize"
      :disabled="disabled || readonly || (min !== undefined && Number(value) <= min)"
      :variant="ButtonVariant.Function"
      @click="sub"
    )
    .input-value.flex.items-center.flex-1.min-w-0.gap-2(:class="{ 'justify-center': stepper }" @click="focusInput")
      input.input(
        ref="input"
        type="text"
        inputmode="decimal"
        :class="{ 'text-center': stepper }"
        :style="unit && stepper ? { width: inputWidth } : undefined"
        :value="displayValue"
        :disabled="disabled || readonly"
        :placeholder="placeholder === '' ? '' : $t(String(placeholder))"
        :min="min"
        :max="max"
        :step="step"
        @focus="inputFocused = true"
        @input="updateDisplayValue"
        @blur="formatDisplayValue"
      )
      span.unit.white-space-nowrap(v-if="unit && stepper") {{ unit }}
    slot(v-if="$slots.default")
    span.unit.white-space-nowrap(v-if="unit && !stepper") {{ unit }}
    .clearable.flex.row-12(v-if="clean && !readonly && !disabled && Boolean(value)")
      Divider(type="vertical" variant="base")
      IconButton.icon(:name="Times" :variant="ButtonVariant.Function" @click="clearValue")
    IconButton.icon(
      v-if="showStepper"
      :name="Plus"
      :size="stepprIconSize"
      :disabled="disabled || readonly || (max !== undefined && max <= Number(value))"
      :variant="ButtonVariant.Function"
      @click="add"
    )
</template>

<style scoped src="../../../styles/css/input-sizes.css"></style>

<style scoped src="../../../styles/css/input-field.css"></style>

<style scoped>
.unit {
  color: var(--cue-content-neutral-tertiary);
}

.input {
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
